import { ExternalLink, FolderCog, Info, Plus, Shapes } from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { FilterChip, KpiTile, Note, Panel } from '@/components/redesign/bits'
import { SearchField } from '@/components/ui/search-field'
import { Segmented } from '@/components/ui/segmented'
import { Skeleton } from '@/components/ui/skeleton'
import { EmptyState, ErrorState } from '@/components/ui/states'
import { PageTitle } from '@/app/layout'
import { cn } from '@/lib/cn'
import { formatDate, numberFmt } from '@/lib/format'
import { type HelpFilters, type HelpKind, type HelpStatus, useHelpArticles } from './api'
import { SectionsDialog } from './sections-dialog'

/**
 * The help center's guides, troubleshooting articles and FAQs on support.arvaseo.com (redesign
 * of 2026-10-16): counts, the articles, and the sections beside them.
 */
export function HelpPage() {
  const navigate = useNavigate()
  const [filters, setFilters] = useState<HelpFilters>({ kind: '', status: '', q: '' })
  const [managing, setManaging] = useState(false)
  const articles = useHelpArticles(filters)
  const all = useHelpArticles({})
  const meta = articles.data?.meta ?? all.data?.meta
  const stats = all.data?.meta.stats

  const every = all.data?.data ?? []
  const published = every.filter((a) => a.status === 'published').length
  const drafts = every.length - published
  const unsorted = every.filter((a) => a.category === null).length
  const count = (kind: HelpKind) => every.filter((a) => a.kind === kind).length

  return (
    <>
      <PageTitle
        title="Help center"
        description="Guides, troubleshooting and FAQs on support.arvaseo.com. Publishing is instant; drafts stay here until you publish them."
        actions={
          <>
            {meta && (
              <a href={meta.support_url} target="_blank" rel="noreferrer" className="btn">
                <ExternalLink aria-hidden="true" />
                View help center
              </a>
            )}
            {meta?.can_manage_categories && (
              <button type="button" className="btn" onClick={() => setManaging(true)}>
                <FolderCog aria-hidden="true" />
                Sections
              </button>
            )}
            <Link to="/help/new" className="btn primary">
              <Plus aria-hidden="true" />
              New article
            </Link>
          </>
        }
      />

      {all.data && (
        <div className="kpis">
          <KpiTile
            label="Published"
            value={numberFmt.format(published)}
            compare={`${numberFmt.format(drafts)} ${drafts === 1 ? 'draft' : 'drafts'}`}
          />
          <KpiTile
            label="Views this month"
            value={stats ? numberFmt.format(stats.views_this_month) : '—'}
            compare={stats ? `${numberFmt.format(stats.views_last_month)} last month` : 'not counted yet'}
          />
          <KpiTile
            label="Found it helpful"
            value={stats ? helpfulShare(stats.helpful_this_month, stats.not_helpful_this_month) : '—'}
            compare={
              stats
                ? `${answers(stats.helpful_this_month + stats.not_helpful_this_month)} this month · ${helpfulShare(stats.helpful_last_month, stats.not_helpful_last_month)} last month`
                : 'not counted yet'
            }
          />
          <KpiTile
            label="Searches with no answer"
            value={stats ? numberFmt.format(stats.misses_this_month) : '—'}
            compare={stats ? `${numberFmt.format(stats.misses_last_month)} last month` : 'not counted yet'}
          />
          <KpiTile
            label="Sections"
            value={numberFmt.format(all.data.meta.categories.length)}
            compare={
              unsorted === 0
                ? 'every article has one'
                : `${numberFmt.format(unsorted)} ${unsorted === 1 ? 'article' : 'articles'} in none`
            }
          />
        </div>
      )}

      <div className="split">
        <section className="sec">
          <div className="fbar">
            <SearchField
              label="Search titles"
              placeholder="Search titles"
              value={filters.q ?? ''}
              onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            />
            <Segmented<HelpStatus | ''>
              label="Status"
              value={filters.status ?? ''}
              onChange={(status) => setFilters((f) => ({ ...f, status }))}
              options={[
                { value: '', label: 'All' },
                { value: 'published', label: 'Published' },
                { value: 'draft', label: 'Drafts' },
              ]}
            />
            <FilterChip
              label="Type"
              icon={<Shapes aria-hidden="true" />}
              value={filters.kind || null}
              onChange={(v) => setFilters((f) => ({ ...f, kind: (v ?? '') as HelpKind | '' }))}
              options={[
                { value: 'guide', label: 'Guides', count: count('guide') },
                { value: 'troubleshooting', label: 'Troubleshooting', count: count('troubleshooting') },
                { value: 'faq', label: 'FAQs', count: count('faq') },
              ]}
            />
          </div>

          {articles.isPending ? (
            <Skeleton className="h-64 rounded-[14px]" />
          ) : articles.isError ? (
            <div className="card">
              <ErrorState error={articles.error} onRetry={() => void articles.refetch()} />
            </div>
          ) : articles.data.data.length === 0 ? (
            <div className="card">
              <EmptyState
                title="Nothing here yet"
                description="Write a guide, a troubleshooting article or an FAQ. It's live on support.arvaseo.com as soon as you publish it."
              />
            </div>
          ) : (
            <div className="tw">
              <table className="t" style={{ minWidth: 560 }}>
                <caption className="sr-only">Help articles</caption>
                <thead>
                  <tr>
                    <th>Article</th>
                    <th>Status</th>
                    <th className="r">Views, 30 days</th>
                    <th className="r">Helpful</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {articles.data.data.map((a) => (
                    <tr key={a.id} className="click" onClick={() => void navigate(`/help/${a.id}`)}>
                      <td>
                        <Link
                          to={`/help/${a.id}`}
                          style={{ fontWeight: 600, color: 'var(--ink)' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {a.title}
                        </Link>
                        <small>
                          {a.category?.name ?? 'No section'} · {meta?.kinds[a.kind] ?? a.kind}
                        </small>
                      </td>
                      <td>
                        <span className={cn('chip', a.status === 'published' ? 'ok' : 'warn')}>
                          {a.status === 'published' ? 'Published' : 'Draft'}
                        </span>
                      </td>
                      <td className="r num">
                        {a.status === 'published' ? numberFmt.format(a.views_30d ?? 0) : '—'}
                      </td>
                      <td
                        className="r num"
                        title={`${numberFmt.format(a.helpful_30d ?? 0)} yes, ${numberFmt.format(a.not_helpful_30d ?? 0)} no in 30 days`}
                      >
                        {helpfulShare(a.helpful_30d ?? 0, a.not_helpful_30d ?? 0)}
                      </td>
                      <td className="m" style={{ whiteSpace: 'nowrap' }}>
                        {a.updated_at ? formatDate(a.updated_at) : '—'}
                        {a.updated_by && <small>{a.updated_by}</small>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <div className="sec">
          <Panel
            title="Sections"
            sub="How the help center groups articles"
            extra={
              meta?.can_manage_categories && (
                <button type="button" className="btn sm ghost" onClick={() => setManaging(true)}>
                  Edit
                </button>
              )
            }
          >
            {(meta?.categories.length ?? 0) === 0 ? (
              <p className="empty-line">No sections yet.</p>
            ) : (
              meta?.categories.map((c) => (
                <div className="srow" key={c.id}>
                  <div>
                    <b>{c.name}</b>
                    {c.description && <small>{c.description}</small>}
                  </div>
                  <b className="num">{numberFmt.format(c.articles)}</b>
                </div>
              ))
            )}
          </Panel>
          <Panel
            title="Searches with no answer"
            sub={
              stats
                ? `What people looked for and didn’t find, last 30 days (counted since ${formatDate(stats.counting_since)})`
                : undefined
            }
          >
            {!stats || stats.top_misses.length === 0 ? (
              <p className="empty-line">None yet.</p>
            ) : (
              stats.top_misses.map((m) => (
                <div className="srow" key={m.query}>
                  <div>
                    <b>{m.query}</b>
                    <small>
                      {numberFmt.format(m.searches)} {m.searches === 1 ? 'search' : 'searches'}
                    </small>
                  </div>
                  <Link to={`/help/new?title=${encodeURIComponent(m.query)}`} className="btn sm ghost">
                    Write
                  </Link>
                </div>
              ))
            )}
          </Panel>
          <Panel
            title="Least helpful"
            sub="Answered No most often in the last 30 days: the ones to rewrite first"
          >
            {!stats || stats.least_helpful.length === 0 ? (
              <p className="empty-line">No article was answered No yet.</p>
            ) : (
              stats.least_helpful.map((a) => (
                <div className="srow" key={a.id}>
                  <div>
                    <b>{a.title}</b>
                    <small>
                      {numberFmt.format(a.not_helpful)} no · {numberFmt.format(a.helpful)} yes
                    </small>
                  </div>
                  <Link to={`/help/${a.id}`} className="btn sm ghost">
                    Edit
                  </Link>
                </div>
              ))
            )}
          </Panel>
          <Note calm icon={<Info aria-hidden="true" />} title="How these are counted">
            One view per visit to an article, and one miss per search that found nothing, a day each. Nothing
            about the person is kept, no cookie is set, and bots and link previews aren’t counted. Helpful is
            the share of Yes among the Yes and No answers at the end of an article, since October 20, 2026.
          </Note>
        </div>
      </div>

      {meta && <SectionsDialog open={managing} onOpenChange={setManaging} categories={meta.categories} />}
    </>
  )
}

/** "80%" of the answers were Yes, or "—" with none. */
function helpfulShare(yes: number, no: number): string {
  const total = yes + no
  return total === 0 ? '—' : `${Math.round((yes / total) * 100)}%`
}

function answers(n: number): string {
  return `${numberFmt.format(n)} ${n === 1 ? 'answer' : 'answers'}`
}
